import { Fragment } from 'react';
import { cn } from '@/lib/cn';
import type { Block, Inline } from '../lib/blog';

/**
 * Renders a blog post's structured body (see lib/blog/types.ts).
 *
 * One switch, one look per block kind — new posts get consistent typography for
 * free. Colours reuse the landing palette: headings `landing-slate`, body
 * `landing-outline` (darker than `landing-pale` because this is long-form copy),
 * accents `primary-500`.
 */

export function InlineText({ text }: { text: Inline }) {
  if (typeof text === 'string') return <>{text}</>;
  return (
    <>
      {text.map((r, i) => {
        switch (r.k) {
          case 'strong':
            return (
              <strong key={i} className="font-semibold text-landing-slate">
                {r.v}
              </strong>
            );
          case 'em':
            return <em key={i}>{r.v}</em>;
          case 'link': {
            const external = /^https?:/.test(r.href);
            return (
              <a
                key={i}
                href={r.href}
                {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
                className="font-medium text-landing-accent underline underline-offset-2 hover:no-underline"
              >
                {r.v}
              </a>
            );
          }
          default:
            return <Fragment key={i}>{r.v}</Fragment>;
        }
      })}
    </>
  );
}

function FlowArrow() {
  return (
    <span
      aria-hidden="true"
      className="flex items-center justify-center text-2xl leading-none text-primary-500"
    >
      <span className="md:hidden">↓</span>
      <span className="hidden md:inline">→</span>
    </span>
  );
}

function BlogBlock({ block }: { block: Block }) {
  switch (block.kind) {
    case 'p':
      return block.lead ? (
        <p className="mb-6 text-xl font-medium leading-[1.6] text-landing-slate lg:text-[22px]">
          <InlineText text={block.text} />
        </p>
      ) : (
        <p className="mb-6 text-[17px] leading-[1.75] text-landing-outline lg:text-lg">
          <InlineText text={block.text} />
        </p>
      );

    case 'h2':
      return (
        <div
          id={block.id}
          className="mb-6 mt-16 scroll-mt-20 border-t border-landing-card-line pt-12 first:mt-0 first:border-t-0 first:pt-0 lg:scroll-mt-28"
        >
          {block.eyebrow && (
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.12em] text-landing-accent">
              {block.eyebrow}
            </p>
          )}
          <h2 className="m-0 text-[26px] font-bold leading-[1.2] tracking-[-0.5px] text-landing-slate lg:text-[34px]">
            {block.text}
          </h2>
        </div>
      );

    case 'h3':
      return (
        <h3 className="mb-4 mt-12 flex items-baseline gap-4 text-xl font-bold leading-[1.3] text-landing-slate lg:text-2xl">
          {block.number && (
            <span className="shrink-0 text-[28px] font-bold leading-none text-primary-500 lg:text-[34px]">
              {block.number}
            </span>
          )}
          <span>{block.text}</span>
        </h3>
      );

    case 'quote':
      return (
        <figure className="my-10 rounded-r-2xl border-l-4 border-primary-500 bg-landing-peach px-6 py-6 lg:px-8 lg:py-7">
          <blockquote className="m-0 text-xl font-semibold leading-[1.45] text-landing-slate lg:text-2xl">
            {block.text}
          </blockquote>
          {block.cite && (
            <figcaption className="mt-4 text-sm font-medium text-landing-deep-muted">
              — {block.cite}
            </figcaption>
          )}
        </figure>
      );

    case 'voices':
      return (
        <ul className="my-8 flex list-none flex-col gap-3 p-0">
          {block.items.map((item) => (
            <li
              key={item}
              className="rounded-2xl rounded-bl-sm border border-landing-card-line bg-landing-band px-5 py-4 text-lg italic leading-[1.5] text-landing-slate"
            >
              “{item}”
            </li>
          ))}
        </ul>
      );

    case 'stats':
      return (
        <div className="my-10">
          <ul className="m-0 grid list-none gap-4 p-0 sm:grid-cols-3">
            {block.items.map((item) => (
              <li
                key={item.value}
                className="flex flex-col gap-2 rounded-2xl border border-landing-card-line bg-white p-5 shadow-sm"
              >
                <p className="m-0 text-[40px] font-bold leading-none tracking-[-1px] text-primary-500">
                  {item.value}
                </p>
                <p className="m-0 text-sm leading-[1.5] text-landing-deep-muted">{item.label}</p>
              </li>
            ))}
          </ul>
          {block.note && <p className="mt-3 text-xs text-landing-deep-muted">{block.note}</p>}
        </div>
      );

    case 'table':
      return (
        <figure className="my-10">
          <div className="overflow-x-auto rounded-2xl border border-landing-card-line">
            <table className="w-full min-w-[560px] border-collapse text-left text-[15px]">
              <thead className="bg-landing-demo-band text-white">
                <tr>
                  {block.head.map((h) => (
                    <th key={h} scope="col" className="px-5 py-3.5 font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row) => (
                  <tr
                    key={row[0]}
                    className="border-t border-landing-card-line even:bg-landing-band"
                  >
                    {row.map((cell, i) =>
                      i === 0 ? (
                        <th
                          key={i}
                          scope="row"
                          className="px-5 py-3.5 font-semibold text-landing-slate"
                        >
                          {cell}
                        </th>
                      ) : (
                        <td key={i} className="px-5 py-3.5 text-landing-outline">
                          {cell}
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <figcaption className="mt-3 text-sm text-landing-deep-muted">{block.caption}</figcaption>
        </figure>
      );

    case 'list':
      return (
        <ul className="mb-6 list-disc space-y-2 pl-6 text-[17px] leading-[1.7] text-landing-outline lg:text-lg">
          {block.items.map((item, i) => (
            <li key={i}>
              <InlineText text={item} />
            </li>
          ))}
        </ul>
      );

    case 'flow':
      return (
        <figure className="my-10">
          <div className="grid gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-stretch">
            {block.stages.map((stage, i) => (
              <Fragment key={stage.title}>
                {i > 0 && <FlowArrow />}
                <div
                  className={cn(
                    'flex flex-col justify-center gap-2 rounded-2xl p-5 text-center',
                    stage.highlight
                      ? 'bg-landing-demo-band text-white shadow-lg ring-2 ring-primary-500'
                      : 'border border-landing-card-line bg-landing-band',
                  )}
                >
                  <p
                    className={cn(
                      'm-0 text-base font-bold',
                      stage.highlight ? 'text-primary-300' : 'text-landing-slate',
                    )}
                  >
                    {stage.title}
                  </p>
                  <p
                    className={cn(
                      'm-0 text-sm leading-[1.5]',
                      stage.highlight ? 'text-white/90' : 'text-landing-deep-muted',
                    )}
                  >
                    {stage.body}
                  </p>
                </div>
              </Fragment>
            ))}
          </div>
          <figcaption className="mt-3 text-sm text-landing-deep-muted">{block.caption}</figcaption>
        </figure>
      );

    case 'cards':
      return (
        <ul className="my-8 grid list-none gap-4 p-0 sm:grid-cols-2">
          {block.items.map((card) => (
            <li
              key={card.title}
              className="flex flex-col gap-2 rounded-2xl border border-landing-card-line bg-white p-5 shadow-sm"
            >
              <span
                aria-hidden="true"
                className="mb-1 block h-1 w-10 rounded-full bg-primary-500"
              />
              <p className="m-0 text-lg font-bold text-landing-slate">{card.title}</p>
              <p className="m-0 text-[15px] leading-[1.6] text-landing-deep-muted">{card.body}</p>
            </li>
          ))}
        </ul>
      );

    case 'steps':
      return (
        <ol className="my-8 flex list-none flex-col gap-6 p-0">
          {block.items.map((step, i) => (
            <li key={step.title} className="flex gap-4">
              <span
                aria-hidden="true"
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary-500 text-base font-bold text-white"
              >
                {i + 1}
              </span>
              <div>
                <p className="m-0 mb-1 text-lg font-bold text-landing-slate">{step.title}</p>
                <p className="m-0 text-[17px] leading-[1.7] text-landing-outline">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      );
  }
}

export function BlogBlocks({ blocks }: { blocks: readonly Block[] }) {
  return (
    <>
      {blocks.map((block, i) => (
        <BlogBlock key={i} block={block} />
      ))}
    </>
  );
}
